import { useState, useEffect } from "react";
import { getConfig, sendTestWebhook } from "../services/gridApi";

export default function Settings() {
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [webhookStatus, setWebhookStatus] = useState<string | null>(null);
  const [sendingWebhook, setSendingWebhook] = useState(false);

  useEffect(() => {
    setLoading(true);
    getConfig()
      .then(setConfig)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleTestWebhook = async () => {
    setSendingWebhook(true);
    setWebhookStatus(null);
    try {
      await sendTestWebhook();
      setWebhookStatus("Test webhook sent successfully!");
    } catch (e: any) {
      setWebhookStatus(`Error: ${e.message}`);
    } finally {
      setSendingWebhook(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-[#F8F8F7] tracking-tight">
          Settings
        </h1>
        <p className="text-sm text-[#8A8A8A] mt-1">
          Platform configuration from Grid API
        </p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Platform Config - Live from API */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#F8F8F7] mb-1">
            Platform Configuration
          </h2>
          <p className="text-sm text-[#8A8A8A] mb-5">
            Live data from GET /config
          </p>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[#8A8A8A]">
              <div className="w-4 h-4 border-2 border-[#00B3E0] border-t-transparent rounded-full animate-spin" />
              Loading platform config...
            </div>
          ) : error ? (
            <div className="text-sm text-red-400">{error}</div>
          ) : config ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-[#8A8A8A] mb-1">Environment</div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-sm text-[#F8F8F7] font-medium">
                      Sandbox
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[#8A8A8A] mb-1">API Version</div>
                  <div className="text-sm text-[#CCCCCC] font-mono">
                    2025-10-13
                  </div>
                </div>
              </div>

              {/* Show raw config */}
              <div>
                <div className="text-xs text-[#8A8A8A] mb-2">Raw Config Response</div>
                <pre className="text-xs font-mono text-[#CCCCCC] bg-black/50 p-4 rounded-lg overflow-auto max-h-80">
                  {JSON.stringify(config, null, 2)}
                </pre>
              </div>
            </div>
          ) : null}
        </div>

        {/* Webhook Testing */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#F8F8F7] mb-1">
            Webhook Testing
          </h2>
          <p className="text-sm text-[#8A8A8A] mb-5">
            Send a test webhook to verify your webhook configuration
          </p>

          <div className="space-y-4">
            <button
              onClick={handleTestWebhook}
              disabled={sendingWebhook}
              className="bg-[#00B3E0] text-black font-semibold px-4 py-2 rounded-lg hover:bg-[#00C4F5] transition-colors disabled:opacity-50"
            >
              {sendingWebhook ? "Sending..." : "Send Test Webhook"}
            </button>

            {webhookStatus && (
              <div
                className={`text-sm ${
                  webhookStatus.startsWith("Error")
                    ? "text-red-400"
                    : "text-emerald-400"
                }`}
              >
                {webhookStatus}
              </div>
            )}
          </div>
        </div>

        {/* Webhook Events */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#F8F8F7] mb-1">
            Webhook Events
          </h2>
          <p className="text-sm text-[#8A8A8A] mb-5">
            Grid sends the following webhook event types
          </p>
          <div className="grid grid-cols-2 gap-2">
            {[
              "incoming-payment",
              "outgoing-payment",
              "kyc-status",
              "account-status",
              "bulk-upload",
              "invitation-claimed",
              "test-webhook",
            ].map((evt) => (
              <div
                key={evt}
                className="flex items-center gap-2 p-2.5 rounded bg-white/[0.02]"
              >
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-sm text-[#CCCCCC] font-mono">{evt}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Signature Verification */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#F8F8F7] mb-1">
            Webhook Signature Verification
          </h2>
          <p className="text-sm text-[#8A8A8A] mb-5">
            Grid uses P-256 ECDSA signatures in the{" "}
            <code className="font-mono text-[#CCCCCC]">X-Grid-Signature</code>{" "}
            header
          </p>

          <div className="p-4 rounded-lg bg-black/50 border border-[#222222]">
            <pre className="text-xs font-mono text-[#CCCCCC] whitespace-pre-wrap">
{`// Verify webhook signature (Node.js)
const crypto = require('crypto');

function verifySignature(payload, signature, publicKey) {
  const verify = crypto.createVerify('SHA256');
  verify.update(payload);
  return verify.verify(publicKey, signature, 'base64');
}`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
